import { PageTypeEnum, WebsitePageType } from 'common/enums/PageTypeEnum'
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircleIcon } from 'src/editor/shared/icons/check-circle-icon'
import { DotsVerticalIcon } from 'src/editor/shared/icons/dots-vertical-icon'
import { File04Icon } from 'src/editor/shared/icons/file-04-icon'
import { Settings02Icon } from 'src/editor/shared/icons/settings-02-icon'
import { XCloseIcon } from 'src/editor/shared/icons/x-close-icon'
import { ActionMenu } from 'src/editor/shared/ui-lib/action-menu'
import { GhostIconButton } from 'src/editor/shared/ui-lib/buttons'
import { DeletePageModal } from 'src/editor/shared/ui-lib/page-element/components/delete-page-modal'
import { Radio } from 'src/editor/shared/ui-lib/radio'
import { WebsiteSettings } from 'src/editor/shared/ui-lib/website-settings'
import { PageActionsEnum } from 'src/shared/enums/website-enums'
import { twMerge } from 'tailwind-merge'

export interface PageElementProps {
  name: string
  type: WebsitePageType
  pageId?: number
  isActive?: boolean
  isSelected?: boolean
  onSelect?: (type: WebsitePageType) => void
}

const pageTypeIcons = {
  [PageTypeEnum.WebsiteHome]: <File04Icon className="fill-iconSecondary" />,
  [PageTypeEnum.WebsiteDefault]: (
    <File04Icon className="fill-iconSecondary" /> // unconfirmed
  ),
  [PageTypeEnum.WebsiteLayout]: (
    <File04Icon className="fill-iconSecondary" /> // unconfirmed
  ),
}

export const PageElement = ({
  name,
  type,
  pageId,
  isActive = false,
  isSelected = false,
  onSelect,
}: PageElementProps) => {
  const { t } = useTranslation()
  const [isWebsiteSettingsOpened, setIsWebsiteSettingsOpened] = useState(false)
  const [isWebsiteRemoveModalOpened, setIsWebsiteRemoveModalOpened] =
    useState(false)

  const items = useMemo(
    () => [
      {
        id: PageActionsEnum.Settings,
        label: t('website_settings.sidebar.pages_management.settings'),
        iconBefore: () => <Settings02Icon />,
      },
      {
        id: PageActionsEnum.Publish,
        label: t('website_settings.sidebar.pages_management.publish'),
        iconBefore: () => <CheckCircleIcon />,
      },
      {
        id: PageActionsEnum.Remove,
        label: t('components.core.actions.remove'),
        iconBefore: () => <XCloseIcon />,
      },
    ],
    [t],
  )

  const handlePageAction = useCallback((id: PageActionsEnum) => {
    switch (id) {
      case PageActionsEnum.Settings:
        setIsWebsiteSettingsOpened(true)
        return
      case PageActionsEnum.Publish:
        // Handle publish action
        return
      case PageActionsEnum.Remove:
        setIsWebsiteRemoveModalOpened(true)
        return
      default:
        return
    }
  }, [])

  return (
    <article
      onClick={() => onSelect?.(type)}
      className={twMerge(
        'flex justify-between rounded-md p-3 hover:bg-gray-50',
        isActive && 'bg-gray-50',
        onSelect && 'cursor-pointer',
      )}
    >
      <div className="inline-flex flex-row items-center gap-1.5">
        {pageTypeIcons[type]}
        <span className="text-M text-textPrimary">{name}</span>
      </div>
      {!!pageId && (
        <>
          <ActionMenu
            items={items}
            onSelect={handlePageAction}
            triggerElement={setIsOpened => (
              <GhostIconButton
                onClick={() => setIsOpened(true)}
                icon={<DotsVerticalIcon className="fill-iconSecondary" />}
              />
            )}
          />
          <WebsiteSettings
            pageId={pageId}
            isOpened={isWebsiteSettingsOpened}
            onClose={() => setIsWebsiteSettingsOpened(false)}
          />
          <DeletePageModal
            pageId={pageId}
            pageName={name}
            isOpened={isWebsiteRemoveModalOpened}
            onClose={() => setIsWebsiteRemoveModalOpened(false)}
          />
        </>
      )}
      {onSelect && <Radio name={`radio-${name}`} isChecked={isSelected} />}
    </article>
  )
}
